// src/utils/subscriptionApi.ts
import { buildApiUrl } from './apiConfig';
import { jsonGet, jsonSend } from './http';
import { mockPlans, mockSubscription, mockInvoices, useMockData } from './subscriptionApiMock';
import { GroupedPaymentPlansResponse } from '../types/groupedPaymentPlans';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  priceId: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  features: string[];
  recommended?: boolean;
  badge?: string;
  maxAthletes?: number;
}

export interface Subscription {
  id: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete';
  planTier: string;
  planName: string;
  planId?: string;
  currentTierLevel?: number | null;
  currentMaxAthletes?: number | null;
  maxAthletes?: number | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialEnd: string | null;
  amount: number;
  currency: string;
  interval: 'month' | 'year' | 'day';
  intervalCount?: number | null;
  paymentMethodSummary?: string;
  paymentMethod?: PaymentMethodDetails | null;
  gracePeriodEnd?: string | null;
  /** School/org display name for "{School Name} Plan" labeling. */
  organizationDisplayName?: string | null;
  /** "family" or "organization" */
  planCategory?: 'family' | 'organization' | string | null;
  /** Customer-friendly plan metadata from Stripe (excludes internal keys). */
  displayMetadata?: Record<string, string> | null;
}

export interface PaymentMethodDetails {
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
  billingDetails: BillingDetails | null;
}

export interface BillingDetails {
  name: string | null;
  email: string | null;
  phone: string | null;
  address: Address | null;
}

export interface Address {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface Invoice {
  id: string;
  number?: string; // Invoice number from Stripe
  amountPaid: number;
  currency: string;
  status: string;
  created: string;
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

/**
 * Response from creating a Stripe Customer Portal session
 */
export interface PortalSessionResponse {
  portalUrl: string;
}

/**
 * Backend response format (what the API actually returns)
 */
interface BackendPlanResponse {
  plans: {
    priceId: string;
    amount: number;
    currency: string;
    interval: 'month' | 'year';
    productId: string;
    productName: string;
    productDescription: string;
    metadata: {
      maxAthletes?: string;
      [key: string]: any;
    };
  }[];
}

/**
 * Payment plan from /api/subscriptions/payment-plans endpoint
 */
export interface PaymentPlan {
  priceId: string;
  amount: number; // in cents
  currency: string;
  interval: string; // "month" or "year"
  productName: string;
  productDescription: string;
  features?: string[]; // Optional features list
  maxAthletes?: number; // Maximum number of athletes allowed
}

/**
 * Fetch all available subscription plans
 */
export async function getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  // Use mock data if enabled
  if (useMockData) {
    return Promise.resolve(mockPlans);
  }

  try {
    const url = buildApiUrl('/api/subscriptions/payment-plans');
    const response = await jsonGet<BackendPlanResponse>(url);

    // Check if response has plans array
    if (!response || !response.plans || !Array.isArray(response.plans)) {
      throw new Error('Invalid response format from server');
    }

    // Transform backend format to frontend format
    const plans: SubscriptionPlan[] = response.plans.map((plan) => {
      // Extract features from metadata if available, otherwise use default features
      const features = plan.metadata?.features || getDefaultFeatures(plan.productName);

      // Extract maxAthletes from metadata (used for tier comparison)
      const maxAthletes = plan.metadata?.maxAthletes ? parseInt(plan.metadata.maxAthletes, 10) : undefined;

      return {
        id: plan.priceId,
        name: plan.productName,
        description: plan.productDescription || getDefaultDescription(plan.productName),
        priceId: plan.priceId,
        price: plan.amount, // Backend returns amount in cents
        currency: plan.currency.toLowerCase(),
        interval: plan.interval,
        features: features,
        recommended: plan.productName.toLowerCase().includes('pro'), // Mark Pro as recommended
        badge: plan.productName.toLowerCase().includes('pro') ? 'Popular' : undefined,
        maxAthletes: maxAthletes
      };
    });

    return plans;
  } catch (error: any) {
    // If backend is not implemented, return mock data as fallback
    if (error.status === 404 || error.status === 500) {
      return mockPlans;
    }

    throw error;
  }
}

/**
 * Helper function to generate default descriptions for plans
 */
function getDefaultDescription(planName: string): string {
  const descriptions: Record<string, string> = {
    'basic': 'Perfect for getting started with your athletic journey',
    'pro': 'Most popular for serious athletes',
    'enterprise': 'For elite athletes and teams seeking maximum advantage',
    'elite': 'For elite athletes seeking maximum advantage',
    'polarisathletehub': 'Access to our Polaris Athlete Web Application and Service'
  };

  return descriptions[planName.toLowerCase()] || `${planName} plan for athletes`;
}

/**
 * Helper function to generate default features for plans
 */
function getDefaultFeatures(planName: string): string[] {
  const features: Record<string, string[]> = {
    'basic': [
      'Access to basic features',
      'Profile management',
      'Task tracking',
      'Email support'
    ],
    'pro': [
      'All Basic features',
      'Advanced analytics',
      'Priority support',
      'Custom branding',
      'Team collaboration'
    ],
    'enterprise': [
      'All Pro features',
      'Dedicated account manager',
      'Custom integrations',
      'Advanced reporting',
      'Unlimited team members'
    ],
    'elite': [
      'All Pro features',
      'Dedicated account manager',
      'Custom integrations',
      'Advanced reporting',
      'Unlimited team members'
    ],
    'polarisathletehub': [
      'Full platform access',
      'Profile management',
      'Task tracking',
      'Calendar integration',
      'Team collaboration',
      'Analytics dashboard',
      'Email support'
    ]
  };

  return features[planName.toLowerCase()] || [
    'Full platform access',
    'Core features included',
    'Email support'
  ];
}

/**
 * Fetch all available payment plans from the payment-plans endpoint
 * This is used for signup/checkout flows
 */
export async function getPaymentPlans(): Promise<PaymentPlan[]> {
  try {
    const url = buildApiUrl('/api/subscriptions/payment-plans');
    const response = await jsonGet<BackendPlanResponse>(url);

    if (!response || !Array.isArray(response.plans)) {
      throw new Error('Invalid response format from server');
    }

    // Transform backend format to include maxAthletes from metadata
    return response.plans.map(plan => ({
      priceId: plan.priceId,
      amount: plan.amount,
      currency: plan.currency,
      interval: plan.interval,
      productName: plan.productName,
      productDescription: plan.productDescription,
      maxAthletes: plan.metadata?.maxAthletes ? parseInt(plan.metadata.maxAthletes, 10) : undefined
    }));
  } catch (error: any) {
    throw error;
  }
}

export interface GetGroupedPaymentPlansOptions {
  /** When true, only family-tier plans are returned from the API. */
  familyOnly?: boolean;
  limit?: number;
}

/**
 * Fetch grouped payment plans from the new endpoint
 * Returns plans grouped by product with multiple price options per plan
 */
export async function getGroupedPaymentPlans(
  options: GetGroupedPaymentPlansOptions = {}
): Promise<GroupedPaymentPlansResponse> {
  try {
    const params = new URLSearchParams();
    if (options.familyOnly) {
      params.set('familyOnly', 'true');
    }
    if (options.limit != null) {
      params.set('limit', String(options.limit));
    }
    const query = params.toString();
    const url = buildApiUrl(
      `/api/subscriptions/payment-plans/grouped${query ? `?${query}` : ''}`
    );
    const response = await jsonGet<GroupedPaymentPlansResponse>(url);

    if (!response || !Array.isArray(response.plans)) {
      throw new Error('Invalid response format from server');
    }

    return response;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Fetch current user's subscription status
 */
export async function getSubscriptionStatus(): Promise<Subscription | null> {
  // Use mock data if enabled
  if (useMockData) {
    return Promise.resolve(mockSubscription); // Return mock subscription data
  }

  try {
    const url = buildApiUrl('/api/subscriptions/status');
    const data = await jsonGet<any>(url);

    // Normalize interval values if needed
    // Backend may return "annual" which should be "year" on the frontend
    if (data && data.interval) {
      if (data.interval === 'annual' || data.interval === 'yearly') {
        data.interval = 'year';
      } else if (data.interval === 'monthly') {
        data.interval = 'month';
      } else if (data.interval === 'daily') {
        data.interval = 'day';
      }
    }

    return data as Subscription;
  } catch (error: any) {
    if (error.status === 404) {
      return null; // No subscription found
    }

    // If backend not implemented, return null
    if (error.status === 500) {
      return null;
    }

    throw error;
  }
}

/**
 * Create a Stripe Checkout session for a subscription
 */
export async function createCheckoutSession(
  priceId: string,
  successUrl: string,
  cancelUrl: string,
  recaptchaToken: string
): Promise<CheckoutSessionResponse> {
  const url = buildApiUrl('/api/subscriptions/checkout');
  return jsonSend<CheckoutSessionResponse>(url, 'POST', {
    priceId,
    successUrl,
    cancelUrl,
    recaptchaToken
  });
}

/**
 * Create a Stripe Checkout session after user registration
 * @param userId - The ID of the newly registered user
 * @param planId - The selected subscription plan ID
 * @param billingInterval - Billing interval (month or year)
 */
export async function createCheckoutSessionAfterRegistration(
  userId: string,
  planId: string,
  billingInterval: 'month' | 'year'
): Promise<CheckoutSessionResponse> {
  const url = buildApiUrl('/api/billing/create-checkout-session');
  return jsonSend<CheckoutSessionResponse>(url, 'POST', {
    userId,
    planId,
    billingInterval
  });
}

/**
 * Create a Stripe Customer Portal session
 * Allows users to upgrade/downgrade plans, update payment methods, cancel subscriptions, and view billing history
 * @param returnUrl - The URL where users will be redirected after completing their session in the Customer Portal
 * @returns Promise<PortalSessionResponse> - Contains the Stripe-hosted Customer Portal URL
 */
export async function createPortalSession(returnUrl: string): Promise<PortalSessionResponse> {
  const url = buildApiUrl('/api/subscriptions/create-portal-session');
  return jsonSend<PortalSessionResponse>(url, 'POST', {
    returnUrl
  });
}

/**
 * Response from canceling a subscription
 */
export interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
  effectiveDate: string;
}

/**
 * Response from upgrading a subscription
 */
export interface UpgradeSubscriptionResponse {
  subscriptionId: string;
  newPriceId: string;
  newPlanName: string;
  status: string;
  message: string;
}

/**
 * Request body for upgrading a subscription
 */
export interface UpgradeSubscriptionRequest {
  newPriceId: string;
}

/**
 * Cancel user's subscription
 * Cancels at the end of the current billing period
 */
export async function cancelSubscription(): Promise<CancelSubscriptionResponse> {
  const url = buildApiUrl('/api/subscriptions/cancel');
  return jsonSend<CancelSubscriptionResponse>(url, 'POST', {});
}

/**
 * Upgrade user's subscription to a higher tier
 * Applies immediate proration
 */
export async function upgradeSubscription(newPriceId: string): Promise<UpgradeSubscriptionResponse> {
  const url = buildApiUrl('/api/subscriptions/upgrade');
  return jsonSend<UpgradeSubscriptionResponse>(url, 'POST', { newPriceId });
}

/**
 * Reactivate a cancelled subscription
 * Removes the scheduled cancellation
 */
export async function reactivateSubscription(): Promise<CancelSubscriptionResponse> {
  const url = buildApiUrl('/api/subscriptions/reactivate');
  return jsonSend<CancelSubscriptionResponse>(url, 'POST', {});
}

/**
 * Fetch recent invoices for the user
 * Uses the new billing endpoint
 */
export async function getInvoices(limit: number = 10): Promise<Invoice[]> {
  try {
    const url = buildApiUrl(`/api/billing/invoices?limit=${limit}`);
    const response = await jsonGet<any>(url);

    // Backend returns { invoices: [...] } format
    if (response && typeof response === 'object' && 'invoices' in response && Array.isArray(response.invoices)) {
      // Transform backend date field to match frontend Invoice interface
      return response.invoices.map((invoice: any) => ({
        id: invoice.id,
        number: invoice.number,
        amountPaid: invoice.amount, // Backend sends cents, use as-is
        currency: invoice.currency || 'usd',
        status: invoice.status,
        created: invoice.date, // Backend uses 'date' field
        invoicePdf: invoice.invoicePdf,
        hostedInvoiceUrl: invoice.hostedInvoiceUrl || null
      }));
    }

    // If response is already an array (shouldn't happen but handle it)
    if (Array.isArray(response)) {
      return response;
    }

    // Unexpected format
    return [];
  } catch (error: any) {
    throw error;
  }
}

/**
 * Verify checkout session after successful payment
 */
export async function verifyCheckoutSession(sessionId: string): Promise<Subscription> {
  const url = buildApiUrl(`/api/subscriptions/verify-session/${sessionId}`);
  return jsonGet<Subscription>(url);
}

/**
 * Response from billing verification for onboarding flow
 */
export interface BillingVerificationResponse {
  planName: string;
  amount: number;
  currency: string;
  billingInterval: 'month' | 'year';
  nextPaymentDate: string | null;
  trialEnd: string | null;
  status: string;
}

/**
 * Verify billing session after onboarding checkout
 */
export async function verifyBillingSession(sessionId: string): Promise<BillingVerificationResponse> {
  const url = buildApiUrl(`/api/billing/verify-session/${sessionId}`);
  return jsonGet<BillingVerificationResponse>(url);
}

/**
 * Response from getting a SetupIntent for payment method update
 */
export interface SetupIntentResponse {
  clientSecret: string;
}

/**
 * Get a SetupIntent client secret for updating payment method
 * This is used to securely collect new card details via Stripe Elements
 */
export async function getSetupIntent(): Promise<SetupIntentResponse> {
  const url = buildApiUrl('/api/subscriptions/payment-method');
  return jsonSend<SetupIntentResponse>(url, 'PUT', {});
}

/**
 * Response from confirming payment method update
 */
export interface ConfirmPaymentMethodResponse {
  success: boolean;
  message: string;
  data?: any;
}

/**
 * Confirm payment method update after Stripe confirmation
 * @param paymentMethodId - The payment method ID returned from Stripe confirmCardSetup
 */
export async function confirmPaymentMethodUpdate(paymentMethodId: string): Promise<ConfirmPaymentMethodResponse> {
  const url = buildApiUrl('/api/subscriptions/payment-method/confirm');
  return jsonSend<ConfirmPaymentMethodResponse>(url, 'POST', {
    paymentMethodId
  });
}
